/**
 * Entity configuration for all 11 bridge entity types.
 * Drives DataTable columns, form fields, API paths, and filters.
 */
import React from 'react';
import { Play, Video } from 'lucide-react';

// Field types: text, number, textarea, select, toggle, date, datetime, image, entity, tags, json

// Us/Them grid cells: show a score when the game has a result/final OR the
// value itself is a real non-zero number (so a freshly typed inline score is
// visible immediately instead of collapsing to "—").
function renderScore(row, key) {
  const v = row[key];
  const n = v === null || v === undefined || v === '' ? null : Number(v);
  const scored = (row.result && String(row.result).trim() !== '') || row.is_final;
  const show = n !== null && !Number.isNaN(n) && n !== -1 && (scored || n !== 0);
  return show ? <span className="text-slate-100">{n}</span> : <span className="text-slate-500">—</span>;
}

export const ENTITIES = {
  dashboard: { key: 'dashboard', label: 'Dashboard', icon: 'LayoutDashboard', plural: 'Dashboard', apiPath: null, description: 'Read-only overview of this org: live count tiles (Games, Teams, Players, Sports, Schedules, Venues, Staff) plus quick actions. Every number is pulled live from the same OL data the other tabs show — nothing is hardcoded. To change a number, edit the tab it comes from (e.g. Sports, Teams).' },

  games: {
    key: 'games', label: 'Game', icon: 'Gamepad2', plural: 'Games', apiPath: '/games',
    description: 'Every game: schedule + opponent + date/time + scores + up to 3 media links. Each game belongs to a Schedule (season + team) and pulls the opponent & venue from the Teams and Venues tabs. Edit scores here; changes push to the WordPress scoreboard.',
    columns: [
      { key: 'title', label: 'Title', sortable: true, maxWidthClass: 'max-w-[150px]' },
      { key: 'unix_dtg', label: 'Date/Time', sortable: true, type: 'datetime' },
      { key: 'opponent', label: 'Opponent', render: (r) => {
          const name = r.opponent || r.title?.replace(/^(VB|VG|JVB|JVG|MSB|MSG|MSB-D)\s*(@|vs\.?)\s*/i, '') || '—';
          if (r.opponent_logo_url) {
            return (
              <span className="flex items-center gap-2 max-w-[150px]" title={name}>
                <img src={r.opponent_logo_url} alt={name}
                  className="h-5 w-5 rounded-full object-cover bg-white flex-shrink-0"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                <span className="min-w-0 truncate">{name}</span>
              </span>
            );
          }
          return (
            <span className="flex items-center gap-2 max-w-[150px]" title={name}>
              <span className="h-5 w-5 rounded-full bg-slate-700 text-[10px] font-bold text-slate-400 flex items-center justify-center flex-shrink-0">
                {(name || '?').slice(0, 2).toUpperCase()}
              </span>
              <span className="min-w-0 truncate">{name}</span>
            </span>
          );
        } },
      { key: 'is_home_game', label: 'H/A', type: 'homeaway' },
      { key: 'is_final', label: 'Final', type: 'boolean' },
      { key: 'our_score', label: 'Us', type: 'number', render: (row) => renderScore(row, 'our_score') },
      { key: 'opp_score', label: 'Them', type: 'number', render: (row) => renderScore(row, 'opp_score') },
      { key: 'result', label: 'Result', maxWidthClass: 'max-w-[150px]' },
      { key: 'media', label: 'Media', render: (row) => {
        const m = Array.isArray(row.media) ? row.media.filter(x => x && x.url) : [];
        if (!m.length) return <span className="text-slate-500">—</span>;
        const src = (url) => /facebook|fb\.watch/i.test(url) ? 'FB' : (/youtu/i.test(url) ? 'YT' : 'Media');
        return (
          <span className="flex flex-wrap gap-1.5" data-testid="game-film-links">
            {m.map((v, i) => (
              <a key={i} href={v.url} target="_blank" rel="noopener noreferrer"
                 title={`${v.label || 'Watch film'} · ${v.url}`}
                 onClick={(e) => e.stopPropagation()}
                 data-testid="game-film-link"
                 className="inline-flex items-center gap-1 text-[11px] font-semibold text-rose-300 hover:text-rose-200 bg-rose-500/10 hover:bg-rose-500/20 px-1.5 py-0.5 rounded transition-colors">
                <Video className="w-3 h-3" />
                <Play className="w-2.5 h-2.5" />
                {src(v.url)}
              </a>
            ))}
          </span>
        );
      } },
    ],
    filters: [
      { key: 'schedule', label: 'Schedule', type: 'entity', entity: 'schedules', paramKey: 'schedule' },
    ],
    // ── Form layout mirrors the MSTW "Game Data" metabox EXACTLY ──
    // Section headings, field order, labels and help text match the
    // WordPress MSTW Game edit screen 1:1 so an operator sees the same
    // form in OL as in wp-admin. Field KEYS remain the OL canonical keys
    // that map to MSTW meta via the bridge (see OL_WP_PUBLISHER.md §2):
    //   schedule_slug→game_sched_id, unix_dtg→game_unix_dtg,
    //   opponent_team_id→opponent team, is_home_game→game_is_home_game,
    //   venue_id→game_gl_location, our/opp_score→game_our/opp_score,
    //   is_final→game_is_final, result→game_result,
    //   media_url_1/2/3 + media_label_1/2/3, curr_period/curr_time.
    formSections: [
      { title: 'Game Data', fields: [
        { key: 'title', label: 'Title', type: 'text', required: true,
          help: 'The game post title. On WordPress this is auto-built from the schedule + opponent; edit it here to override.' },
        { key: 'schedule_slug', label: 'Select Schedule', type: 'schedule_picker',
          help: 'The schedule (season + team) this game belongs to.' },
        { key: 'unix_dtg', label: 'Date & Time', type: 'game_datetime', hideLabel: true, required: true },
        { key: 'time_tba', label: 'Time TBA', type: 'select',
          options: [{ v: '', l: 'Set' }, { v: 'TBA', l: 'TBA' }, { v: 'TBD', l: 'TBD' }],
          help: 'A TBA is anything other than a set time. Choose TBA/TBD when the start time is undecided — the front end shows the label instead of a time.' },
        { key: 'opponent_team_slug', label: 'Opponent', type: 'entity', entity: 'teams',
          optionFilter: 'opponents', optionValue: 'slug', syncLabelTo: 'opponent',
          help: "Assign the opponent from the Teams database (real opponents only — the Sabres' own teams and event rows are hidden). The opponent's logo, short name and contact data are pulled from the Teams DB." },
        { key: 'is_home_game', label: 'Home Game', type: 'checkbox',
          help: 'Check for home games. This normally determines the game venue.' },
        { key: 'venue_slug', label: 'Game Location', type: 'entity', entity: 'venues',
          optionValue: 'slug', syncLabelTo: 'location',
          help: "USE FOR HOME or NEUTRAL-SITE GAMES. Normally the home venue & the opponent team's contact data are pulled from the Teams DB." },
        { key: 'is_event', label: 'Non-game event (rally, banquet, tryout)', type: 'checkbox',
          help: 'Check to mark this row as a non-competition event (pep rally, banquet, tryout, ceremony…) so it is hidden when "Matchups only" is on. Leave unchecked for a real game.' },
      ]},
      { title: 'Game Result Data',
        description: 'Game result data fields, primarily for the scoreboard.',
        fields: [
          { key: 'our_score', label: 'Home Score', type: 'number',
            help: "Score for the team assigned to the scoreboard as the 'home' team. The 'Home Score' label can be changed in the Display Settings." },
          { key: 'opp_score', label: 'Opponent Score', type: 'number',
            help: 'Opponent team score.' },
          { key: 'is_final', label: 'Final Score', type: 'checkbox',
            help: 'Check this box when the game score is final.' },
          { key: 'curr_period', label: 'Current Period', type: 'text',
            help: 'For games in progress. Current period (or quarter) — leave blank when the game is final.' },
          { key: 'curr_time', label: 'Time Remaining', type: 'text',
            help: 'For games in progress. Current time remaining in the game — leave blank when the game is final.' },
          { key: 'result', label: 'Game Result', type: 'result_text' },
        ]},
      { title: 'Links to Game Information',
        description: 'Links to game information. Originally for "media" links, but can now be repurposed and used in ways to suit your site\'s needs.',
        fields: [
          { key: 'media_label_1', label: 'Media Links Label 1', type: 'text',
            help: 'This one will be displayed for Media Links link 1. If it is blank, MEDIA WILL NOT BE DISPLAYED.' },
          { key: 'media_url_1', label: 'Media Links URL 1', type: 'text',
            help: 'URL for Media Links link 1.' },
          { key: 'media_label_2', label: 'Media Links Label 2', type: 'text',
            help: 'This one will be displayed for Media Links link 2. If it is blank, it will not be displayed.' },
          { key: 'media_url_2', label: 'Media Links URL 2', type: 'text',
            help: 'URL for Media Links link 2.' },
          { key: 'media_label_3', label: 'Media Links Label 3', type: 'text',
            help: 'This one will be displayed for Media Links link 3. If it is blank, it will not be displayed.' },
          { key: 'media_url_3', label: 'Media Links URL 3', type: 'text',
            help: 'URL for Media Links link 3.' },
        ]},
    ],
  },

  schedules: {
    key: 'schedules', label: 'Schedule', icon: 'Calendar', plural: 'Schedules', apiPath: '/schedules',
    description: 'One team\'s season fixture list, identified by a canonical slug (e.g. bk-vb-2026 = 2026-2027). The slug is the grouping key for Games — every game belongs to a schedule, and the public team-page schedule table is built from it. Each schedule depends on a Sport + Team; pick the Sport/Level/Year and OmniLeagues builds the slug. OL Mongo is the source of truth; WordPress is a downstream projection.',
    columns: [
      { key: 'title', label: 'Title', sortable: true },
      { key: 'team_title', label: 'Team', sortable: true,
        tooltip: 'The home team this schedule belongs to. Assign the team at the level indicated by the title.',
        editAs: { field: 'team_id', resolve: { entity: 'teams', field: 'title' }, valueField: 'ol_id' },
        render: (r) => r.team_title || <span className="text-slate-500">—</span> },
    ],
    filters: [],
    formSections: [
      { title: 'Basic', fields: [
        { key: 'title', label: 'Title', type: 'text', required: true },
      ]},
      { title: 'Scope', description: 'Pick Sport + Level + Season (+ Term for volleyball/baseball) and OmniLeagues builds the canonical slug (e.g. vl-vg-2026-fall). Slug is hidden.', fields: [
        { key: 'sport_id', label: 'Sport', type: 'entity', entity: 'sports', optionValue: 'ol_id', help: 'The sport this schedule belongs to — drives the slug prefix (bk/bb/vl) and the season list.' },
        { key: 'level', label: 'Level', type: 'vocab_select', vocab: 'levels', help: 'Team level+gender (§1.2): Varsity Boys, JV Girls…' },
        { key: 'year', label: 'Season', type: 'season_select', help: 'Only the current season and next (pre-season setup) are offered, per sport. Stored as the academic year.' },
        { key: 'term', label: 'Term', type: 'select', help: 'Volleyball uses Fall/Winter; baseball uses Spring; basketball uses none. Maroon/White split two same-level squads.', options: [
          { v: '', l: 'None (basketball)' }, { v: 'fall', l: 'Fall' }, { v: 'winter', l: 'Winter' },
          { v: 'spring', l: 'Spring' }, { v: 'summer', l: 'Summer' },
          { v: 'maroon', l: 'Maroon (variant)' }, { v: 'white', l: 'White (variant)' },
        ]},
        { key: 'team_id', label: 'Team', type: 'entity', entity: 'teams', optionValue: 'ol_id', help: 'The home team this schedule belongs to (its games group under this schedule).' },
      ]},
      { title: 'League', description: 'Optional. The conference/association this schedule plays in — pushed to WordPress as the schedule\'s league term. Manage leagues in the Leagues tab.', fields: [
        { key: 'league_id', label: 'League', type: 'entity', entity: 'leagues', optionValue: 'id', optionLabel: 'name', help: 'Optional conference/association (e.g. NCHEAC). Stores the league internally; WordPress receives its league term.' },
      ]},
      { title: 'Display', fields: [
        { key: 'default_time', label: 'Default Game Time', type: 'text', placeholder: '19:00', help: '24-hour local start time applied to new games unless overridden (e.g. 19:00).' },
      ]},
    ],
  },

  teams: {
    key: 'teams', label: 'Team', icon: 'Users', plural: 'Teams', apiPath: '/teams',
    description: 'Every team — the Sabres\' own squads (one per Sport + Level, so "Varsity Boys" exists separately for Basketball and Baseball) and all opponents. A team holds identity, its Sport and home Venue, so add those first. Teams are referenced by Schedules and Games; the Sport/Home Venue columns show friendly names resolved server-side, never a raw id. OL Mongo is the source of truth; WordPress is a downstream projection.',
    columns: [
      { key: 'logo_url', label: '', type: 'thumbnail', render: (r) => {
          // Grid thumbnail renders logo_url (GridFS SOT, OL_LOGO §1); alt_logo_url
          // is only a fallback when the primary is empty. Never show alt first —
          // it often holds a dead legacy-host URL (sws.semprion.com / wp-content).
          const src = r.logo_url || r.alt_logo_url;
          if (!src) return <div className="h-7 w-7 rounded bg-slate-600/50" />;
          return <img src={src} alt="" className="h-7 w-7 rounded object-cover bg-slate-700" onError={e => { e.currentTarget.style.display = 'none'; }} />;
        } },
      { key: 'title', label: 'Title', sortable: true },
      { key: 'short_name', label: 'Short Name' },
      { key: '_ol_meta.opponent_type', label: 'OL Type', tooltip: 'OL-only opponent classification (homeschool / private / nationals / public). Drives stats splits.', render: r => {
        const t = (r._ol_meta && r._ol_meta.opponent_type) || '';
        if (!t) return <span className="text-slate-600 italic text-xs">—</span>;
        const colors = {
          homeschool: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
          private:    'bg-blue-500/20 text-blue-300 border-blue-500/30',
          nationals:  'bg-purple-500/20 text-purple-300 border-purple-500/30',
          public:     'bg-amber-500/20 text-amber-300 border-amber-500/30',
          other:      'bg-slate-500/20 text-slate-300 border-slate-500/30',
        };
        const auto = r._ol_meta && r._ol_meta.auto_classified ? '*' : '';
        return <span className={`px-1.5 py-0.5 rounded border text-[10px] uppercase ${colors[t] || colors.other}`}>{t}{auto}</span>;
      } },
      { key: '_ol_meta.is_conference', label: 'Conf.', tooltip: 'OL-only conference flag (used by stats engine).', render: r => (
        r._ol_meta && r._ol_meta.is_conference
          ? <span className="text-amber-300 font-bold">✓</span>
          : <span className="text-slate-600">—</span>
      ) },
      { key: 'sport_id', label: 'Sport', tooltip: 'The sport this team plays. Set in WP under the team post.',
        // Display the server-resolved sport NAME (never the raw sport_id UUID);
        // inline-edit flips through the sports picker via editAs and WRITES the
        // sport OL uuid (valueField:'ol_id') — the ONE stored format. See WP_TEAMS §3.
        editAs: { field: 'sport_id', resolve: { entity: 'sports', field: 'title' }, valueField: 'ol_id' },
        render: (r) => r.sport_title || <span className="text-slate-500">—</span> },
      { key: 'home_venue_id', label: 'Home Venue', tooltip: 'The default home venue for this team. Maps to a WP Venue post.',
        // Display the server-resolved venue NAME (never a bare id/number);
        // inline-edit flips through the venues picker via editAs and WRITES the
        // venue OL uuid (valueField:'ol_id') — the ONE stored format, matching
        // the modal form picker. See WP_TEAMS §3.
        editAs: { field: 'home_venue_id', resolve: { entity: 'venues', field: 'title' }, valueField: 'ol_id' },
        render: (r) => r.home_venue_title || <span className="text-slate-500">—</span> },
    ],
    filters: [
      { key: 'sport', label: 'Sport', type: 'entity', entity: 'sports', paramKey: 'sport' },
    ],
    formSections: [
      { title: 'Identity', fields: [
        { key: 'title', label: 'Title', type: 'text', required: true },
        { key: 'full_name', label: 'Full Name', type: 'text' },
        { key: 'short_name', label: 'Short Name', type: 'text' },
        { key: 'nickname', label: 'Nickname', type: 'text' },
        { key: 'full_mascot', label: 'Full Mascot', type: 'text' },
        { key: 'short_mascot', label: 'Short Mascot', type: 'text' },
      ]},
      { title: 'Classification', description: 'Own squads only: pick Sport + Level so the canonical slug (e.g. bk-vb) builds automatically. Opponents: leave Level blank (slug = title).', fields: [
        { key: 'sport_id', label: 'Sport', type: 'entity', entity: 'sports', optionValue: 'ol_id' },
        { key: 'other_sport_ids', label: 'Also plays', type: 'entity_multi', entity: 'sports', optionValue: 'ol_id', excludeKey: 'sport_id',
          help: 'Other sports this same team plays (one team row, never a duplicate). OL-only.' },
        { key: 'level', label: 'Level', type: 'vocab_select', vocab: 'levels', help: 'Own-squad level+gender (Varsity Boys, JV Girls…). Leave blank for opponents.' },
      ]},
      { title: 'OL Classification (OL-only — never sent to WP)',
        badge: 'OL only',
        description: 'Used by the OmniLeagues stats engine for HS/Private/Nationals splits and conference filters. These fields live in OL Mongo and are not projected to WordPress.',
        fields: [
          { key: '_ol_meta.opponent_type', label: 'Opponent Type', type: 'select', options: [
            { v: '', l: '— unclassified —' },
            { v: 'homeschool', l: 'Homeschool' },
            { v: 'private',    l: 'Private' },
            { v: 'nationals',  l: 'Nationals' },
            { v: 'public',     l: 'Public' },
            { v: 'other',      l: 'Other' },
          ]},
          { key: '_ol_meta.is_conference', label: 'Conference Opponent', type: 'checkbox',
            help: 'Mark if this team is in your league/conference (used by stats filters).' },
          { key: '_ol_meta.notes', label: 'Notes (OL only)', type: 'textarea' },
        ]
      },
      { title: 'Branding', fields: [
        { key: 'logo_url', label: 'Team Logo (41x28 — primary badge)', type: 'image', metaField: 'team_logo' },
        { key: 'alt_logo_url', label: 'Alt Logo (125x125 — website/app slider)', type: 'image', metaField: 'team_alt_logo' },
      ]},
      { title: 'Relationships', fields: [
        { key: 'home_venue_id', label: 'Home Venue', type: 'entity', entity: 'venues', optionValue: 'ol_id' },
        { key: 'link', label: 'Website', type: 'text' },
      ]},
    ],
    headerActions: [
      { key: 'auto-classify', label: 'Auto-classify Teams', icon: 'Sparkles',
        confirmTitle: 'Run auto-classifier?',
        confirmDescription: 'Scans every WP team and assigns an opponent_type based on name keywords (e.g. "Christian" → private, "Homeschool" → homeschool, "National" → nationals). Will NOT overwrite teams you have already classified by hand. Safe to re-run.',
        endpoint: '/api/team-meta/{org_id}/auto-classify',
      },
    ],
  },

  venues: {
    key: 'venues', label: 'Venue', icon: 'MapPin', plural: 'Venues', apiPath: '/venues',
    description: 'The places games are played — name, address, map & website. Games use a Venue as their location and Teams reference a home venue, so add the venue here first. Courts at the same complex are separate venues.',
    columns: [
      { key: 'title', label: 'Title', sortable: true },
      { key: 'address', label: 'Address', render: (r) => {
          // separately; OL-canonical rows have a single pre-joined `address`.
          // Avoid double-printing city/state.
          let text = '';
          if (r.address_1) {
            text = [r.address_1, r.city, r.state, r.zip]
              .map(s => (s || '').trim()).filter(Boolean).join(', ');
          } else if (r.address) {
            text = r.address;
          } else {
            text = [r.city, r.state, r.zip]
              .map(s => (s || '').trim()).filter(Boolean).join(', ');
          }
          if (!text) return <span className="text-slate-500">—</span>;
          return <span className="text-slate-100 truncate max-w-[420px] block" title={text}>{text}</span>;
        } },
      { key: 'map_url', label: 'Map', type: 'link' },
      { key: 'website', label: 'Website', type: 'link' },
    ],
    filters: [],
    formSections: [
      { title: 'Identity', fields: [
        { key: 'title', label: 'Title', type: 'text', required: true },
        { key: 'address_1', label: 'Address 1', type: 'text' },
        { key: 'address_2', label: 'Address 2', type: 'text' },
        { key: 'city', label: 'City', type: 'text' },
        { key: 'state', label: 'State', type: 'text' },
        { key: 'zip', label: 'Zip', type: 'text' },
        { key: 'country', label: 'Country', type: 'text' },
      ]},
      { title: 'Contact', fields: [
        { key: 'map_url', label: 'Map URL', type: 'text' },
        { key: 'website', label: 'Website', type: 'text' },
      ]},
    ],
  },

  sports: {
    key: 'sports', label: 'Sport', icon: 'Trophy', plural: 'Sports', apiPath: '/sports',
    description: 'Sports are the root of everything: Basketball, Baseball, Volleyball. The SLUG is the internal 2-char code (bk/bb/vl) — the public site always shows the full name. Teams, Schedules, Players, Staff and Games all hang off a Sport, so add/rename here first.',
    columns: [
      { key: 'title', label: 'Title', sortable: true },
      { key: 'scoring_model', label: 'Scoring' },
      { key: 'period_count', label: 'Periods' },
      { key: 'period_name', label: 'Period Name' },
    ],
    filters: [],
    formSections: [
      { title: 'Basics', fields: [
        { key: 'title', label: 'Title', type: 'text', required: true },
      ]},
      { title: 'Period Structure', fields: [
        { key: 'period_name', label: 'Period Name', type: 'text', placeholder: 'Quarter' },
        { key: 'period_name_plural', label: 'Period Name Plural', type: 'text' },
        { key: 'period_count', label: 'Period Count', type: 'number' },
        { key: 'period_length_minutes', label: 'Period Length (min)', type: 'number' },
        { key: 'uses_time_clock', label: 'Uses Time Clock', type: 'toggle' },
        { key: 'uses_shot_clock', label: 'Uses Shot Clock', type: 'toggle' },
      ]},
      { title: 'Overtime & Shootout', collapsed: true, fields: [
        { key: 'supports_overtime', label: 'Supports Overtime', type: 'toggle' },
        { key: 'overtime_name', label: 'Overtime Name', type: 'text' },
        { key: 'overtime_length_minutes', label: 'Overtime Length (min)', type: 'number' },
        { key: 'supports_shootout', label: 'Supports Shootout', type: 'toggle' },
      ]},
      { title: 'Scoring', fields: [
        { key: 'scoring_model', label: 'Scoring Model', type: 'select', options: [
          { v: 'points', l: 'Points' }, { v: 'runs', l: 'Runs' }, { v: 'goals', l: 'Goals' },
          { v: 'sets', l: 'Sets' }, { v: 'time', l: 'Time' }, { v: 'match_points', l: 'Match Points' },
          { v: 'strokes', l: 'Strokes' }, { v: 'team_meet', l: 'Team Meet' },
        ]},
        { key: 'has_line_score', label: 'Has Line Score', type: 'toggle' },
      ]},
      { title: 'Structure', collapsed: true, fields: [
        { key: 'game_format', label: 'Game Format', type: 'select', options: [
          { v: 'head_to_head', l: 'Head to Head' }, { v: 'multi_team_meet', l: 'Multi-Team Meet' },
          { v: 'tournament', l: 'Tournament' }, { v: 'individual', l: 'Individual' },
        ]},
        { key: 'roster_size_min', label: 'Roster Size Min', type: 'number' },
        { key: 'roster_size_max', label: 'Roster Size Max', type: 'number' },
      ]},
      { title: 'Branding', collapsed: true, fields: [
        { key: 'logo_url', label: 'Sport Icon', type: 'image', metaField: 'sport_icon' },
      ]},
    ],
  },

  players: {
    key: 'players', label: 'Player', icon: 'UserCircle', plural: 'Players', apiPath: '/players',
    description: 'The player roster — name, number, position, year and current team/level. The Team column shows a friendly label (e.g. "Baseball · Varsity Boys"); edit it to move a player between SWS teams. Jersey and team changes are written to the player\u2019s membership and pushed to WordPress.',
    columns: [
      { key: 'photo_url', label: '', type: 'thumbnail' },
      { key: 'title', label: 'Name', sortable: true, render: (r) => `${r.first_name || ''} ${r.last_name || ''}`.trim() || r.title },
      { key: 'number', label: '#', sortable: true, render: (r) => {
          const v = r.number || r.jersey_number;
          return v ? <span className="font-mono text-slate-100">{v}</span> : <span className="text-slate-500">—</span>;
        } },
      { key: 'position', label: 'Pos', render: (r) => r.position || r.position_long
          ? <span className="text-slate-100">{r.position || r.position_long}</span>
          : <span className="text-slate-500">—</span> },
      { key: 'year', label: 'Year', render: (r) => r.year || r.year_long
          ? <span className="text-slate-100">{r.year || r.year_long}</span>
          : <span className="text-slate-500">—</span> },
      { key: 'age', label: 'Age', render: (r) => r.age
          ? <span className="text-slate-100">{r.age}</span>
          : <span className="text-slate-500">—</span> },
      { key: 'current_team_label', label: 'Team / Level', sortable: true,
        tooltip: 'Current sport + team assignment from WordPress. Edit to move the player to a different SWS team (e.g. JV Boys → Varsity Boys).',
        // The display column is the friendly label; the actual write
        // target on PATCH is `current_team_id` — the underlying SWS
        // team UUID. `editAs` tells DataTable to render the picker
        // against the live `teams` entity list (already filtered to
        // SWS-managed teams via `resolve.filter`).
        editAs: { field: 'current_team_id',
                  resolve: { entity: 'teams',
                             filter: (t) => (t.code || '').trim() && (t.sport_id || '') } },
        render: (r) => r.current_team_label
          ? <span className="text-slate-100">{r.current_team_label}</span>
          : <span className="text-slate-500">unassigned</span> },
    ],
    filters: [],
    formSections: [
      { title: 'Identity', fields: [
        { key: 'first_name', label: 'First Name', type: 'text' },
        { key: 'last_name', label: 'Last Name', type: 'text' },
        { key: 'number', label: 'Number', type: 'text' },
      ]},
      { title: 'Team Assignment', fields: [
        // ONE Roster Team picker serves BOTH add and edit (AJ). Value = roster
        // SLUG. Add: create_entity writes it into team_memberships. Edit:
        // update_entity moves the membership the user opened (matched by
        // current_team_slug) to this roster, leaving other-sport memberships
        // intact. Options = current ACADEMIC season + future (see RosterPicker),
        // labelled by name. Preselects the player's current_team_slug on edit.
        { key: 'roster_slug', label: 'Roster Team', type: 'roster_picker',
          tooltip: 'Enrol / move the player on a current-season roster. Pick by name; the roster slug is stored on the player\u2019s membership. Other-sport memberships are left untouched.' },
      ]},
      { title: 'Position', fields: [
        { key: 'position', label: 'Position', type: 'text' },
        { key: 'position_long', label: 'Position (Full)', type: 'text' },
      ]},
      { title: 'Details', collapsed: true, fields: [
        { key: 'height', label: 'Height', type: 'text' },
        { key: 'weight', label: 'Weight', type: 'text' },
        { key: 'age', label: 'Age', type: 'text' },
        { key: 'year', label: 'Year', type: 'text', placeholder: 'SR' },
        { key: 'year_long', label: 'Year (Full)', type: 'text', placeholder: 'Senior' },
        { key: 'experience', label: 'Experience', type: 'text' },
      ]},
      { title: 'Background', collapsed: true, fields: [
        { key: 'home_town', label: 'Home Town', type: 'text' },
        { key: 'last_school', label: 'Last School', type: 'text' },
        { key: 'country', label: 'Country', type: 'text' },
      ]},
      { title: 'Photo', collapsed: true, fields: [
        { key: 'photo_url', label: 'Player Photo', type: 'image', metaField: 'player_photo' },
      ]},
    ],
  },

  rosters: {
    key: 'rosters', label: 'Roster Team', icon: 'ClipboardList', plural: 'Roster Teams', apiPath: '/rosters',
    description: 'Read-only list of roster teams (one per team + season, e.g. "Volleyball Varsity Girls 2026 Fall") with player counts. The Name is a friendly label derived from the slug (OL Canonical convention); the slug is the internal id that Players enroll against. Manage rosters/players in WordPress.',
    columns: [
      { key: 'name', label: 'Name', sortable: true },
      { key: 'count', label: 'Players', tooltip: 'Live count computed from OL player memberships enrolled against this roster slug.' },
    ],
    filters: [],
    formSections: [
      { title: 'Compose Roster Team', fields: [
        // STRUCTURED pickers only — the canonical slug is COMPOSED server-side,
        // never typed (hand-typed slugs cause canonical drift). One composer
        // field renders Sport/Level/Year/Term/Variant + a live slug+name preview.
        { key: 'roster_compose', label: '', type: 'roster_composer' },
        { key: 'team_id', label: 'Team', type: 'entity', entity: 'teams', optionValue: 'ol_id',
          help: 'The Sabres team this roster belongs to (roster→team FK). A one-season split (e.g. JV Boys White) links to its own team so it stays distinct on every surface.' },
        { key: 'description', label: 'Description (optional)', type: 'textarea',
          tooltip: 'Optional notes. Does NOT affect the slug or name.' },
      ]},
    ],
    titleField: 'name',
    readOnly: true,   // existing rows are taxonomy-derived (read-only)…
    allowNew: true,   // …but a NEW roster team can be created (OL rosters row + WP mstw_tr_team term)
  },

  seasons: {
    key: 'seasons', label: 'Season', icon: 'CalendarRange', plural: 'Seasons', apiPath: '/seasons',
    description: 'The playing seasons games and schedules belong to (e.g. 2026-2027). A season groups a sport\'s fixtures for a given year; Schedules and Games are tagged with it, and the public site surfaces the current season per the per-sport Season Dates in Tech Settings. OL Mongo is the source of truth; WordPress is a downstream projection.',
    columns: [
      { key: 'name', label: 'Name', sortable: true },
      { key: 'slug', label: 'Slug', type: 'slug' },
      { key: 'count', label: 'Games' },
    ],
    filters: [],
    formSections: [
      { title: 'Identity', fields: [
        { key: 'name', label: 'Name', type: 'text', required: true },
        { key: 'slug', label: 'Slug', type: 'slug' },
        { key: 'description', label: 'Description', type: 'textarea' },
      ]},
    ],
    titleField: 'name',
  },

  leagues: {
    key: 'leagues', label: 'League', icon: 'Shield', plural: 'Leagues', apiPath: '/leagues',
    description: 'The conference/association your teams compete in (e.g. NCHEAC). One canonical row per league: friendly Name, short Code and its Divisions. Referenced by Schedules (a schedule\'s league) — edit a league here and every schedule pointing to it stays in sync. OL Mongo is the source of truth; WordPress is a downstream projection.',
    columns: [
      { key: 'name', label: 'Name', sortable: true },
      { key: 'code', label: 'Code', sortable: true },
      { key: 'divisions', label: 'Divisions', inlineEditable: false,
        render: (r) => Array.isArray(r.divisions) && r.divisions.length
          ? <span className="text-slate-100">{r.divisions.join(', ')}</span>
          : <span className="text-slate-500">—</span> },
    ],
    filters: [],
    formSections: [
      { title: 'Identity', fields: [
        { key: 'name', label: 'Name', type: 'text', required: true, placeholder: 'North Carolina Home Education Athletic Conference' },
        { key: 'code', label: 'Code', type: 'text', placeholder: 'NCHEAC' },
        { key: 'description', label: 'Description', type: 'textarea' },
      ]},
      { title: 'Divisions', description: 'The divisions/conferences within this league.', fields: [
        { key: 'divisions', label: 'Divisions', type: 'text', help: 'Separate each division with a comma, e.g. "Eastern, Western".' },
      ]},
    ],
    titleField: 'name',
  },

  // ───────────── Coaching Staff (WP bridge: /coaches /staff-groups /positions) ─────────────
  'staff-groups': {
    key: 'staff-groups', label: 'Staff Group', icon: 'UsersRound', plural: 'Staff Groups', apiPath: '/staff-groups',
    description: 'Groups every role for one team (or one org body) under a single canonical slug — e.g. "Varsity Boys Basketball Staff" (bk-vb-staff) or "Board of Directors" (org-board-staff). The slug auto-generates from Scope + Sport + Level (team) or Area (org); you never type it. Staff Positions roll up into a group, and the group NAME is the source for the public-site coaching-staff heading. Edit via the pencil (row) editor, not inline. OL Mongo is the source of truth; WordPress is a downstream projection.',
    columns: [
      { key: 'title', label: 'Name', sortable: true, inlineEditable: false, render: r => r.name || r.title || '—' },
      { key: 'sport', label: 'Sport / Scope', inlineEditable: false, render: r => {
        const map = { basketball: 'Basketball', baseball: 'Baseball', volleyball: 'Volleyball', soccer: 'Soccer', football: 'Football' };
        if (r.sport) return <span className="text-[10px] uppercase font-bold text-sky-300 bg-sky-500/15 px-1.5 py-0.5 rounded">{map[r.sport] || r.sport}</span>;
        return <span className="text-[10px] uppercase font-bold text-purple-300 bg-purple-500/15 px-1.5 py-0.5 rounded">{r._scope_label || 'Org'}</span>;
      } },
      { key: 'level_gender', label: 'Level', inlineEditable: false, render: r => {
        const map = { vb: 'Varsity Boys', vg: 'Varsity Girls', vc: 'Varsity Coed', jvb: 'JV Boys', jvbw: 'JV Boys White', jvg: 'JV Girls', msb: 'MS Boys', msg: 'MS Girls', msbd: 'MS Boys Dev', msw: 'MS Boys White' };
        if (!r.level_gender) return <span className="text-slate-500">—</span>;
        return <span className="text-slate-200 text-sm">{map[r.level_gender] || r.level_gender.toUpperCase()}</span>;
      } },
      { key: 'count', label: '#', inlineEditable: false, tooltip: 'Staff positions (roles) in this group — from OmniLeagues.', render: r => (
        <span className="font-mono text-slate-100">{r.count ?? '—'}</span>
      ) },
    ],
    filters: [],
    formSections: [
      { title: 'Identity', fields: [
        { key: 'title', label: 'Title', type: 'text', required: true, placeholder: 'Varsity Boys Basketball Staff' },
        { key: 'slug', label: 'Slug (auto-generated)', type: 'slug', noOverride: true, help: 'Generated from Scope + Sport + Level (team) or Area (org) per the canonical rules. Not editable.' },
        { key: 'content', label: 'Description', type: 'textarea' },
      ]},
      { title: 'Classification', description: 'Scope drives the slug. Team groups pick Sport + Level; organization groups pick an Area.', fields: [
        { key: 'scope', label: 'Scope', type: 'select', required: true, options: [
          { v: 'team', l: 'Team (a sport squad)' }, { v: 'org', l: 'Organization (leadership / body)' },
        ]},
        { key: 'sport', label: 'Sport', type: 'select', showIf: v => (v.scope || 'team') === 'team', options: [
          { v: 'basketball', l: 'Basketball' }, { v: 'baseball', l: 'Baseball' },
          { v: 'volleyball', l: 'Volleyball' }, { v: 'soccer', l: 'Soccer' }, { v: 'football', l: 'Football' },
        ]},
        { key: 'level_gender', label: 'Level', type: 'select', showIf: v => (v.scope || 'team') === 'team', options: [
          { v: 'vb', l: 'Varsity Boys (VB)' }, { v: 'vg', l: 'Varsity Girls (VG)' }, { v: 'vc', l: 'Varsity Coed (VC)' },
          { v: 'jvb', l: 'JV Boys (JVB)' }, { v: 'jvbw', l: 'JV Boys White (JVBW)' }, { v: 'jvg', l: 'JV Girls (JVG)' },
          { v: 'msb', l: 'MS Boys (MSB)' }, { v: 'msg', l: 'MS Girls (MSG)' },
          { v: 'msbd', l: 'MS Boys Development (MSBD)' }, { v: 'msw', l: 'MS Boys White (MSW)' },
        ]},
        { key: 'area', label: 'Area', type: 'select', showIf: v => v.scope === 'org', optionsFrom: 'staff_areas',
          help: 'The part of the organization this group belongs to (editable in Tech Settings → Staff Roles).' },
      ]},
    ],
    titleField: 'title',
  },

  coaches: {
    key: 'coaches', label: 'Staff', icon: 'UserCog', plural: 'Staff', apiPath: '/coaches',
    description: 'The PEOPLE in the organization (called "Coaches" in WordPress) — both current staff and past-season staff (the Season column shows which season each row belongs to). Every row — current or past — is fully editable, downloadable and deletable. Open a person to edit their bio/headshot and their Assignments, which FILL existing role slots defined on the Staff Positions tab (grouped by Staff Groups). One person can hold several positions. Create new role slots on Staff Positions, not here. OL Mongo is the source of truth; WordPress is a downstream projection.',
    columns: [
      { key: 'photo_url', label: '', type: 'thumbnail' },
      { key: 'title', label: 'Name', sortable: true, inlineEditable: false, render: (r) => {
          // Coaches in OL Mongo store first_name/last_name; older WP-pulled
          // rows store title. The bridge title is sometimes the role
          // (e.g. "Assistant Coach"), so fall back gracefully.
          const fromNames = [r.first_name, r.last_name].filter(Boolean).join(' ').trim();
          const name = r.display_name || fromNames || r.title || '';
          if (!name) {
            // Final fallback: titlecase the slug if everything else is empty.
            const slug = (r.slug || '').split('-').filter(p => /^[a-z]/i.test(p))
              .map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' ');
            return <span className="text-slate-300">{slug || '—'}</span>;
          }
          return <span className="text-slate-100">{name}</span>;
        } },
      { key: 'sport', label: 'Sport', inlineEditable: false, render: (r) => {
          const map = { bk: 'Basketball', bb: 'Baseball', vl: 'Volleyball' };
          const parts = String(r.sport || '').split(',').map(s => s.trim()).filter(Boolean);
          if (!parts.length) {
            // Leadership members (board/steering) with no assigned sport = "All".
            if (r._leadership_area) return <span className="text-[10px] uppercase font-bold text-purple-300 bg-purple-500/15 px-1.5 py-0.5 rounded" title="Leadership — all sports">All</span>;
            return <span className="text-slate-500">—</span>;
          }
          return <span className="flex flex-wrap gap-1">{parts.map(p => (
            <span key={p} className="text-[10px] uppercase font-bold text-sky-300 bg-sky-500/15 px-1.5 py-0.5 rounded">{map[p] || p}</span>
          ))}</span>;
        } },
      { key: 'role_label', label: 'Role', inlineEditable: false, render: (r) => {
          const v = r.role_label || '';
          if (!v) return <span className="text-slate-500">—</span>;
          const isHead = /head coach/i.test(v);
          return <span title={v} className={`inline-block max-w-[160px] truncate align-middle text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${isHead ? 'text-emerald-300 bg-emerald-500/15' : 'text-indigo-300 bg-indigo-500/15'}`}>{v}</span>;
        } },
      { key: 'team_group', label: 'Group / Team', inlineEditable: false, render: (r) => (
          <span className="block text-slate-300 text-sm max-w-[170px] truncate" title={r.team_group || ''}>{r.team_group || '—'}</span>
        ) },
      { key: 'season', label: 'Season', inlineEditable: false, render: (r) => (
          <span className="text-xs text-slate-300">{r.season || '—'}</span>
        ) },
      { key: 'experience', label: 'Experience', maxWidthClass: 'max-w-[110px]' },
      { key: 'high_school', label: 'High School', maxWidthClass: 'max-w-[120px]' },
    ],
    filters: [],
    formSections: [
      { title: 'Identity', fields: [
        { key: 'title', label: 'Full Name', type: 'text', required: true, placeholder: 'Mike Anderson' },
        { key: 'excerpt', label: 'Tagline / Short Bio', type: 'textarea' },
        { key: 'content', label: 'Full Biography', type: 'textarea' },
      ]},
      { title: 'Leadership Sport', description: 'For Board / Steering Committee members only. Choose which sport this leadership member supports — they appear under that sport\'s tab on the public About page. "All sports" (e.g. the Athletic Director) shows under every tab.', fields: [
        { key: 'leadership_sport', label: 'Sport', type: 'select', optionsFrom: 'sports',
          showIf: v => !!v._leadership_area,
          help: 'Board members are always all-sports; this applies to Steering Committee members.' },
      ]},
      { title: 'Assignments (Sport / Group-Team / Role / Season)', description: 'Assign this staff member to one or more teams/groups with a role and season. The slug is set automatically from the name.', fields: [
        { key: '_assignments', label: 'Team & Role Assignments', hideLabel: true, type: 'staff_assignments' },
      ]},
      { title: 'Background', collapsed: true, description: 'Optional biographical details shown on the public staff/coach page (mapped to the WordPress mstw_cs_* fields).', fields: [
        { key: 'experience', label: 'Years of Experience', type: 'text', help: 'Coaching/among-staff experience, e.g. "12 years" or "8th season". Appears on the public coach card.' },
        { key: 'alma_mater', label: 'Alma Mater', type: 'text', help: 'College/university the staff member attended.' },
        { key: 'degree', label: 'Degree', type: 'text', help: 'Degree earned, e.g. "B.S. Kinesiology".' },
        { key: 'high_school', label: 'High School', type: 'text', help: 'High school attended.' },
        { key: 'birth_date', label: 'Birth Date', type: 'text', help: 'Optional — shown only if your public theme displays it.' },
        { key: 'home_town', label: 'Home Town', type: 'text', help: 'Hometown, e.g. "Raleigh, NC".' },
        { key: 'family', label: 'Family', type: 'textarea', help: 'Short family note, e.g. spouse/children — free text shown on the bio page.' },
      ]},
      { title: 'Headshot', fields: [
        { key: 'photo_url', label: 'Coach Headshot', type: 'image', metaField: 'coach_photo' },
      ]},
    ],
    titleField: 'title',
  },

  positions: {
    key: 'positions', label: 'Staff Position', icon: 'ClipboardCheck', plural: 'Staff Positions', apiPath: '/positions',
    description: 'The role SLOTS on a team or the organization (Head Coach of Varsity Boys, Athletic Director, …) — this is the SOT of who holds each role. Pick Sport, Level and Role in the editor and OmniLeagues builds the canonical slug and links it to WordPress automatically (you never type a slug). Each slot depends on a Staff Group (auto-created) and is filled by a person from the Staff tab. Edit via the pencil (row) editor, not inline. OL Mongo is the source of truth; WordPress is a downstream projection.',
    columns: [
      { key: 'title', label: 'Role', sortable: true, inlineEditable: false, render: r => {
        const t = (r.title || '').toLowerCase();
        const isHead = t.startsWith('head') || t.includes(' head ');
        return (
          <span className="flex items-center gap-1.5">
            {isHead && <span className="text-[9px] uppercase font-bold text-amber-300 bg-amber-500/20 px-1.5 py-0.5 rounded">Head</span>}
            <span className="text-slate-100">{r.title || '—'}</span>
          </span>
        );
      } },
      { key: 'sport', label: 'Sport', inlineEditable: false, render: r => {
        const map = { basketball: 'Basketball', baseball: 'Baseball', volleyball: 'Volleyball', soccer: 'Soccer', football: 'Football' };
        if (!r.sport) return <span className="text-[10px] uppercase font-bold text-purple-300 bg-purple-500/15 px-1.5 py-0.5 rounded" title="Organization-level role (not tied to a sport)">{r._org_name || 'Org'}</span>;
        return <span className="text-[10px] uppercase font-bold text-sky-300 bg-sky-500/15 px-1.5 py-0.5 rounded">{map[r.sport] || r.sport}</span>;
      } },
      { key: 'level', label: 'Level', inlineEditable: false, render: r => {
        const map = { vb: 'Varsity Boys', vg: 'Varsity Girls', jvb: 'JV Boys', jvbw: 'JV Boys White', jvg: 'JV Girls', msb: 'MS Boys', msg: 'MS Girls', msbd: 'MS Boys Dev', msw: 'MS Boys White' };
        if (!r.level) return <span className="text-slate-500">—</span>;
        return <span className="text-slate-200 text-sm">{map[r.level] || r.level.toUpperCase()}</span>;
      } },
      { key: 'coach_id', label: 'Coach', inlineEditable: false, tooltip: 'The staff member assigned to this role.',
        render: r => r._coach_name
          ? <span className="text-slate-100">{r._coach_name}</span>
          : <span className="text-slate-500 italic">unfilled</span> },
      { key: 'season', label: 'Season', inlineEditable: false, render: r => (
        <span className="text-slate-300 text-xs">{r.season || 'All'}</span>
      ) },
    ],
    filters: [
      { key: 'staff_group', label: 'Staff Group', type: 'entity', entity: 'staff-groups', paramKey: 'staff_group' },
    ],
    formSections: [
      { title: 'Classification', description: 'Sport, level and role — the slug is generated automatically from these (you never type it).', fields: [
        { key: 'sport', label: 'Sport', type: 'select', options: [
          { v: '', l: 'Organization (no sport)' },
          { v: 'basketball', l: 'Basketball' }, { v: 'baseball', l: 'Baseball' },
          { v: 'volleyball', l: 'Volleyball' }, { v: 'soccer', l: 'Soccer' },
          { v: 'football', l: 'Football' },
        ], help: 'Leave as "Organization" for board / admin roles that are not tied to a team; pick a sport for a coaching role.' },
        { key: 'level', label: 'Sport Level', type: 'select', showIf: v => !!v.sport, options: [
          { v: '', l: '— none (org role) —' },
          { v: 'vb', l: 'Varsity Boys (VB)' }, { v: 'vg', l: 'Varsity Girls (VG)' },
          { v: 'jvb', l: 'JV Boys (JVB)' }, { v: 'jvbw', l: 'JV Boys White (JVBW)' }, { v: 'jvg', l: 'JV Girls (JVG)' },
          { v: 'msb', l: 'Middle School Boys (MSB)' }, { v: 'msg', l: 'Middle School Girls (MSG)' },
          { v: 'msbd', l: 'MS Boys Development (MSBD)' }, { v: 'msw', l: 'MS Boys White (MSW)' },
        ] },
        { key: 'area', label: 'Area', type: 'select', optionsFrom: 'staff_areas', showIf: v => !v.sport,
          help: 'Which part of the organization this role belongs to (Board, Admin, Support…). Used only for organization roles.' },
        { key: 'role', label: 'Role', type: 'select', required: true, optionsFrom: 'staff_roles',
          help: 'The role list is editable in Tech Settings → Staff Roles. Choose "Assistant Coach" and OmniLeagues assigns the next open assistant slot automatically.' },
      ]},
      { title: 'Assignment', fields: [
        { key: 'coach_id', label: 'Coach / Staff Member', type: 'entity', entity: 'coaches', labelFrom: '_coach_name',
          help: 'Who fills this role. Leave blank to create an unfilled slot.' },
      ]},
    ],
    titleField: 'title',
  },

  // ───────────── Media Library (custom panel — not using DataTable) ─────────────
  media: {
    key: 'media', label: 'Media', icon: 'Image', plural: 'Media Library',
    apiPath: null, // handled by MediaLibraryPanel directly
    customPanel: 'MediaLibraryPanel',
    description: 'The WordPress media library, category-aware. Upload an image, pick a category (team photo, coach, sponsor, hero slide, logo, …) and OmniLeagues computes the canonical WP slug and tells you exactly where it renders on the public site. The slug shown on each card is that theme-slot token (e.g. bk-vb-team-photo-2026), not a database id. Deleting a file makes theme references fall back to the global default. Files upload straight to WordPress; OL mirrors them for fast serving.',
  },

  // 'settings' deliberately omitted from WP Mgmt — all WP/bridge configuration
  // lives under the Tech Specialist's Tech Settings → Site Linking panel
  // (single source of truth, Session 19, May 12 2026).
};

export const ENTITY_NAV_ORDER = [
  'dashboard', 'games', 'schedules', 'teams', 'venues', 'sports',
  'players', 'rosters', 'seasons', 'leagues',
  // Staff workflow grouped at the end in the user's preferred order
  // (Session 24, May 12 2026): people first, then their positions, then
  // the groups those positions roll up into.
  'coaches', 'positions', 'staff-groups',
  'media',
];
